import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../redux/ApiHelper";
import { ParentInterface } from "../../interfaces/parent.interface";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import SelectAdminInput from "./SelectAdminInput";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { IoArrowRedo } from "react-icons/io5";

const formatJoinedDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { dateStyle: "medium" })
    : "N/A";

const ParentList = () => {
  const navigate = useNavigate();
  const [originalList, setOriginalList] = useState<ParentInterface[]>([]);
  const [parentList, setParentList] = useState<ParentInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedParentId, setSelectedParentId] = useState<string>();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("adminId", String(signedAdmin.id));
    if (searchQuery.trim()) {
      queryParams.set("type", "employee");
    } else {
      queryParams.set("type", String(signedAdmin.type));
    }
    get(`/admin/parent/all?${queryParams.toString()}`).then((res) => {
      setOriginalList(res);
      if (!searchQuery.trim()) {
        setParentList(res);
      }
    });
  }, [isModalOpen, searchQuery]);

  const getFranchiseParents = () => {
    const queryParams = new URLSearchParams();
    queryParams.set("adminId", String(signedAdmin.id));
    get(`/admin/parent/franchise-parents?${queryParams.toString()}`).then(
      (res) => {
        setParentList(res);
      }
    );
  };
  // console.log(parentList);
  const handleSearch = () => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      if (signedAdmin.type === "franchise") {
        getFranchiseParents();
        return;
      }
      setParentList(originalList);
      return;
    }

    if (signedAdmin.type === "franchise") {
      const filteredList = originalList.filter((parent) => {
        const lowerQuery = trimmedQuery.toLowerCase();
        return (
          parent.phoneNumber?.toString() === trimmedQuery || // exact match
          parent.email?.toLowerCase() === lowerQuery
        );
      });
      setParentList(filteredList);
    } else {
      const filteredList = originalList.filter((parent) => {
        const lowerQuery = trimmedQuery.toLowerCase();
        return (
          parent._id?.toLowerCase().includes(lowerQuery) ||
          parent.name?.toLowerCase().includes(lowerQuery) ||
          parent.phoneNumber?.toString().includes(lowerQuery) ||
          parent.email?.toLowerCase().includes(lowerQuery) ||
          parent.address?.addressLine?.toLowerCase().includes(lowerQuery) ||
          parent.address?.city?.toLowerCase().includes(lowerQuery) ||
          parent.address?.state?.toLowerCase().includes(lowerQuery) ||
          parent.address?.pinCode?.toString().includes(lowerQuery)
        );
      });
      setParentList(filteredList);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [originalList, searchQuery]);

  // Function to open modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  //   Function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const columns: Column[] = [
    { select: "_id", header: "Id", align: "center" },
    { select: "name", header: "Name", align: "left" },
    {
      select: "createdAt",
      header: "Joined Date",
      render: (value: string) => formatJoinedDate(value),
    },
    {
      select: "phoneNumber",
      header: "Phone Number",
      render: (value, row) => `+${91} ${value}`,
    },
    {
      select: "email",
      header: "Email",
      render: (value) => value || "N/A",
    },
    { select: "password", header: "Password", align: "left" },
    // { select: "dob", header: "Date of Birth" },
    // { select: "aadhar", header: "Aadhar" },
    // { select: "pan", header: "PAN" },
    {
      select: "address",
      header: "Address",
      render: (value) =>
        value
          ? `${value.addressLine}, ${value.city}, ${value.state} ${
              value.pinCode ? "- " + value.pinCode : ""
            }`
          : "N/A",
    },
    {
      select: "students",
      header: "Students",
      render: (value) =>
        value && value.length > 0
          ? value.map((student: any, index: number) => (
              <div key={student._id ?? index}>
                <p className="whitespace-nowrap">
                  {index + 1}.{student.name}
                </p>
              </div>
            ))
          : "NILL",
    },
    {
      select: "admin",
      header: "Admin",
      render: (value, data: ParentInterface) =>
        value ? (
          <div className="flex flex-col">
            <span>{data.admin.name}</span>
            <span>{data.admin.email}</span>
            <span>{data.admin.pin ?? data.admin.pin}</span>
          </div>
        ) : (
          <button
            onClick={() => {
              setSelectedParentId(data._id);
              openModal();
            }}
          >
            Assign admin
          </button>
        ),
    },
    {
      header: "Details",
      render: (_: unknown, row: ParentInterface) => (
        <button
          onClick={() => navigate(`/parent/${row._id}`)}
          className="flex items-center gap-2 p-2 bg-slate-500 text-white text-sm font-medium rounded-full hover:bg-slate-600 transition m-auto"
        >
          <IoArrowRedo className="m-auto" />
        </button>
      ),
    },
  ];

  return (
    <div className="parent table-container">
      <div className="title">Parent List</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
          placeholder={
            signedAdmin.type === "franchise"
              ? "Search by phoneNo or emailId"
              : "Search parent"
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <SelectAdminInput
        isOpen={isModalOpen}
        closeModal={closeModal}
        selectedParentId={selectedParentId}
      ></SelectAdminInput>
      <TableComponent
        columns={columns.filter((item) => {
          if (signedAdmin.type === "franchise" && item.select === "password") {
            return false;
          } else if (
            signedAdmin.type === "employee" &&
            signedAdmin.role === "admin" &&
            item.select === "password"
          ) {
            return false;
          } else {
            return true;
          }
        })}
        data={parentList}
      />
    </div>
  );
};

export default ParentList;
